<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'
import { useDisplay, useTheme } from 'vuetify/framework'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import CollapsibleCell from '@/components/createdUi/CollapsibleCell.vue'
import type { ITableOptions } from '@/modules/apiStore/types/adverse-events/ITableOptions'
import { useAuditArchiveStore } from '@/modules/audits/stores/useAuditArchiveStore/useAuditArchiveStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { formatDateOnly } from '@/utils/formatters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'

const theme = useTheme()
const { smAndDown } = useDisplay()

const feedbackStore = useFeedbackStore()
const archiveStore = useAuditArchiveStore()
const { itemsPerPage } = useTableItemsPerPage('auditArchive', 25)
archiveStore.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  archiveStore.itemsPerPage = newVal
})

defineEmits(['click:prev', 'click:next', 'click:first', 'click:last'])

const displayedAudits = computed(() => archiveStore.audits ?? [])
const currentArchivePage = ref(1)
const isLoadingMore = ref(false)

const hideNextButton = computed(() => {
  const isFetchReady = archiveStore.nextCursor === null
  const isMaxPage = Math.ceil(archiveStore.totalAuditsCount / itemsPerPage.value) <= currentArchivePage.value
  return isFetchReady || isMaxPage
})

// Конфигурация заголовков
const headers = computed(() => [
  { title: '№', key: 'number', sortable: false, maxWidth: '6rem' },
  { title: 'Норматив', key: 'normative', sortable: false, maxWidth: '10rem' },
  { title: 'Требование', key: 'requirement', sortable: false },
  { title: 'Критерии', key: 'criteria' },
  { title: 'Отделение', key: 'department', sortable: false, maxWidth: '10rem' },
  { title: 'Дата нач.', key: 'date_start', sortable: false, maxWidth: '8rem' },
  { title: 'Дата кон.', key: 'date_end', sortable: false, maxWidth: '8rem' },
  { title: 'Вид', key: 'type', sortable: false, maxWidth: '8rem' },
  { title: 'Отчет', key: 'report', maxWidth: '6rem' },
])

// Фильтрация по номеру
const showNumberFilter = ref(false)
const numberFilterModal = ref('')

// Фильтрация по датам
const showDateStartFilter = ref(false)
const showDateEndFilter = ref(false)
const dateStartFilterModal = ref('')
const dateEndFilterModal = ref('')
const dateStartMenu = ref(false)
const dateEndMenu = ref(false)

// Поиск по всем полям
const searchFilter = ref('')

// Меню для UniversalFilterTree
const filterMenus = ref<Record<string, boolean>>({})
const tempSelectedFilters = ref<Record<string, any[]>>({})

// Флаги активности фильтров
const isNumberFilterActive = computed(() => (archiveStore.filters.numbers?.length ?? 0) > 0)
const isNormativeFilterActive = computed(() => (archiveStore.filters.normatives?.length ?? 0) > 0)
const isRequirementFilterActive = computed(() => (archiveStore.filters.requirements?.length ?? 0) > 0)
const isDepartmentFilterActive = computed(() => (archiveStore.filters.departments?.length ?? 0) > 0)
const isDateStartFilterActive = computed(() => archiveStore.filters.date_start !== null)
const isDateEndFilterActive = computed(() => archiveStore.filters.date_end !== null)
const isTypeFilterActive = computed(() => (archiveStore.filters.types?.length ?? 0) > 0)

// Вспомогательные функции
const getFilterIcon = (isActive: boolean) => isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

// Функции для работы с датами
const formatDateForInput = (date: Date | null): string => {
  if (!date) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) return null
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

// Пресеты дат
const datePresets = ref([
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'current_week', label: 'Текущая неделя' },
  { id: 'last_week', label: 'Прошлая неделя' },
  { id: 'current_month', label: 'Текущий месяц' },
  { id: 'last_month', label: 'Прошлый месяц' },
])

const setDatePreset = (preset: string, isStartDate: boolean) => {
  const today = new Date()

  const getStartOfDay = (date: Date) => {
    const start = new Date(date)
    start.setHours(0, 0, 0, 0)
    return start
  }

  const getStartOfWeekMonday = (date: Date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diffToMonday = (day === 0 ? -6 : 1) - day
    d.setDate(d.getDate() + diffToMonday)
    return getStartOfDay(d)
  }

  const addDays = (date: Date, n: number) => {
    const d = new Date(date)
    d.setDate(d.getDate() + n)
    return d
  }

  let dateValue = ''

  switch (preset) {
    case 'today': {
      const start = getStartOfDay(today)
      dateValue = formatDateForInput(start)
      break
    }

    case 'yesterday': {
      const y = addDays(getStartOfDay(today), -1)
      dateValue = formatDateForInput(y)
      break
    }

    case 'current_week': {
      const startOfWeek = getStartOfWeekMonday(today)
      dateValue = formatDateForInput(startOfWeek)
      break
    }

    case 'last_week': {
      const startOfThisWeek = getStartOfWeekMonday(today)
      const startOfLastWeek = addDays(startOfThisWeek, -7)
      dateValue = formatDateForInput(startOfLastWeek)
      break
    }

    case 'current_month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      dateValue = formatDateForInput(getStartOfDay(startOfMonth))
      break
    }

    case 'last_month': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      dateValue = formatDateForInput(getStartOfDay(startOfLastMonth))
      break
    }
  }

  if (isStartDate) {
    dateStartFilterModal.value = dateValue
  } else {
    dateEndFilterModal.value = dateValue
  }
}

const onDateStartSelect = (date: any) => {
  if (date) {
    dateStartFilterModal.value = formatDateForInput(new Date(date))
  }
  dateStartMenu.value = false
}

const onDateEndSelect = (date: any) => {
  if (date) {
    dateEndFilterModal.value = formatDateForInput(new Date(date))
  }
  dateEndMenu.value = false
}

// Основные функции фильтрации
const handleFilterClick = async (filterKey: string) => {
  if (!filterKey) return

  tempSelectedFilters.value[filterKey] = [...((archiveStore.filters as any)[filterKey] || [])]

  await archiveStore.fetchFilterChoices(filterKey)

  filterMenus.value[filterKey] = true
}

const applyFilter = async (filterKey: string) => {
  if (!filterKey) return

  (archiveStore.filters as any)[filterKey] = [...(tempSelectedFilters.value[filterKey] || [])]

  filterMenus.value[filterKey] = false

  triggerServerFilter()
}

const applyNumberFilter = () => {
  if (numberFilterModal.value.trim() === '') {
    archiveStore.filters.numbers = []
  } else {
    archiveStore.filters.numbers = numberFilterModal.value
      .split(/[,;\s]+/)
      .map(num => Number.parseInt(num.trim()))
      .filter(num => !isNaN(num) && num > 0)
      .map(num => String(num))
  }

  triggerServerFilter()
  showNumberFilter.value = false
}

const applyDateStartFilter = () => {
  const date = parseDateFromInput(dateStartFilterModal.value)
  archiveStore.filters.date_start = date ? date.toISOString().substr(0, 10) : null
  triggerServerFilter()
  showDateStartFilter.value = false
}

const applyDateEndFilter = () => {
  const date = parseDateFromInput(dateEndFilterModal.value)
  archiveStore.filters.date_end = date ? date.toISOString().substr(0, 10) : null
  triggerServerFilter()
  showDateEndFilter.value = false
}

const applyAllFieldsFilter = () => {
  if (searchFilter.value.trim() === '') return

  archiveStore.filters.search = searchFilter.value
  searchFilter.value = ''

  triggerServerFilter()
}

const triggerServerFilter = () => {
  archiveStore.nextCursor = null
  currentArchivePage.value = 1
  archiveStore.fetchAudits()
}

// Функции для сброса фильтров
const resetDateStartFilter = () => {
  dateStartFilterModal.value = ''
  archiveStore.filters.date_start = null
}

const resetDateEndFilter = () => {
  dateEndFilterModal.value = ''
  archiveStore.filters.date_end = null
}

const renewDataAndResetFilters = () => {
  resetDateStartFilter()
  resetDateEndFilter()
  archiveStore.filters = {
    numbers: [],
    normatives: [],
    requirements: [],
    departments: [],
    date_start: null,
    date_end: null,
    types: [],
    search: '',
    // Добавляем пустые массивы для полей, которые есть в хранилище
    status: [],
  }
  archiveStore.fetchAudits()
}

// Чипы активных фильтров
const hasActiveFilters = computed(() => {
  return (
    (archiveStore.filters.numbers?.length ?? 0) > 0
    || (archiveStore.filters.normatives?.length ?? 0) > 0
    || (archiveStore.filters.requirements?.length ?? 0) > 0
    || (archiveStore.filters.departments?.length ?? 0) > 0
    || (archiveStore.filters.types?.length ?? 0) > 0
    || archiveStore.filters.date_start !== null
    || archiveStore.filters.date_end !== null
    || archiveStore.filters.search !== ''
  )
})

const activeFilterChips = computed(() => {
  const chips: Array<{ label: string; type: string; value?: any }> = []

  const findLabelRecursive = (nodes: any[], id: any): string | undefined => {
    if (!nodes || !Array.isArray(nodes)) return undefined

    for (const node of nodes) {
      if (String(node.id) === String(id)) {
        return node.label
      }
      if (node.children && node.children.length > 0) {
        const found = findLabelRecursive(node.children, id)
        if (found) return found
      }
    }
    return undefined
  }

  const getLabel = (field: string, id: any) => {
    // В архиве используем те же searchChoices/filterChoices что и в сторе
    // Предположу что стор имеет эти поля. Если нет, то будет undefined, что вернет просто ID.
    let label = findLabelRecursive(archiveStore.searchChoices?.[field], id)
    if (!label) {
      label = findLabelRecursive(archiveStore.filterChoices?.[field], id)
    }
    return label || String(id)
  }

  // Номера
  if (archiveStore.filters.numbers && archiveStore.filters.numbers.length > 0) {
    archiveStore.filters.numbers.forEach((numberValue: string) => {
      chips.push({ label: `Номер: ${numberValue}`, type: 'numbers', value: numberValue })
    })
  }

  // Нормативы
  if (archiveStore.filters.normatives && archiveStore.filters.normatives.length > 0) {
    archiveStore.filters.normatives.forEach((normativeId: string) => {
      chips.push({ label: `Норматив: ${getLabel('normatives', normativeId)}`, type: 'normatives', value: normativeId })
    })
  }

  // Требования
  if (archiveStore.filters.requirements && archiveStore.filters.requirements.length > 0) {
    archiveStore.filters.requirements.forEach((requirementId: string) => {
      chips.push({ label: `Требование: ${getLabel('requirements', requirementId)}`, type: 'requirements', value: requirementId })
    })
  }

  // Отделения
  if (archiveStore.filters.departments && archiveStore.filters.departments.length > 0) {
    archiveStore.filters.departments.forEach((deptId: number) => {
      chips.push({ label: `Отделение: ${getLabel('departments', deptId)}`, type: 'departments', value: deptId })
    })
  }

  // Виды
  if (archiveStore.filters.types && archiveStore.filters.types.length > 0) {
    archiveStore.filters.types.forEach((typeId: string) => {
      chips.push({ label: `Вид: ${getLabel('types', typeId)}`, type: 'types', value: typeId })
    })
  }

  // Дата начала
  if (archiveStore.filters.date_start) {
    chips.push({
      label: `Дата нач.: ${formatDateOnly(archiveStore.filters.date_start)}`,
      type: 'date_start',
    })
  }

  // Дата окончания
  if (archiveStore.filters.date_end) {
    chips.push({
      label: `Дата кон.: ${formatDateOnly(archiveStore.filters.date_end)}`,
      type: 'date_end',
    })
  }

  // Поиск
  if (archiveStore.filters.search) {
    chips.push({ label: `Поиск: ${archiveStore.filters.search}`, type: 'search' })
  }

  return chips
})

const removeFilter = (chip: { label: string; type: string; value?: any }) => {
  switch (chip.type) {
    case 'numbers':
      archiveStore.filters.numbers = archiveStore.filters.numbers.filter((v: string) => v !== String(chip.value))
      numberFilterModal.value = ''
      break
    case 'normatives':
      archiveStore.filters.normatives = archiveStore.filters.normatives.filter((v: string) => v !== chip.value)
      break
    case 'requirements':
      archiveStore.filters.requirements = archiveStore.filters.requirements.filter((v: string) => v !== chip.value)
      break
    case 'departments':
      archiveStore.filters.departments = archiveStore.filters.departments.filter((v: number) => v !== chip.value)
      break
    case 'types':
      archiveStore.filters.types = archiveStore.filters.types.filter((v: string) => v !== chip.value)
      break
    case 'date_start':
      resetDateStartFilter()
      showDateStartFilter.value = false
      break
    case 'date_end':
      resetDateEndFilter()
      showDateEndFilter.value = false
      break
    case 'search':
      searchFilter.value = ''
      archiveStore.filters.search = ''
      break
  }
  triggerServerFilter()
}

function handlePageChange(options: ITableOptions) {
  const page = Number(options.page || 1)
  const perPage = Number(options.itemsPerPage || 25)

  const sortMap: Record<string, string> = {
    number: 'number',
    normative: 'normative',
    requirement: 'requirement',
    department: 'department',
    type: 'type',
    status: 'status',
    date_start: 'date_start',
    date_end: 'date_end',
  }

  let newSortString: string | null = null

  if (options.sortBy && options.sortBy.length > 0) {
    const { key, order } = options.sortBy[0]
    const backendKey = sortMap[key]
    if (backendKey) {
      newSortString = order === 'desc' ? `-${backendKey}` : backendKey
    }
  }

  currentArchivePage.value = page

  if (newSortString !== archiveStore.currentSortString || perPage !== itemsPerPage.value) {
    archiveStore.currentSortString = newSortString
    itemsPerPage.value = perPage
    currentArchivePage.value = 1
    archiveStore.fetchAudits(undefined, perPage)
    return
  }

  const itemsNeeded = page * perPage
  const currentLoaded = archiveStore.audits.length

  if (isLoadingMore.value) return

  if (itemsNeeded > currentLoaded && archiveStore.nextCursor) {
    isLoadingMore.value = true
    let cursorParam: string | null = null

    try {
      cursorParam = new URL(archiveStore.nextCursor).searchParams.get('cursor')
    } catch (e) {
      cursorParam = archiveStore.nextCursor
    }

    archiveStore.fetchAudits(cursorParam ?? undefined, itemsPerPage.value)
      .catch(err => console.error('Ошибка догрузки архива:', err))
      .finally(() => {
        isLoadingMore.value = false
      })
  }
}

const rowProps = () => ({})

const isEmployeeInfoModalOpen = ref(false)
const selectedEmployeeId = ref<number | null>(null)

useOnboardingContext({ names: [AUDITS_CONTEXTS.archive], root: () => document.body })

onMounted(async () => {
  // Предзагрузка если нужна
})
</script>

<template>
  <div class="wrapper">
    <div>
      <!-- v-if/v-else вместо Teleport :disabled — иначе на мобилке дублируются фильтры -->
      <Teleport
        v-if="smAndDown"
        defer
        to="#audit-mobile-filters"
      >
        <VExpansionPanels class="w-100">
          <VExpansionPanel>
            <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-filter-3-line" size="20" />
                <span class="text-subtitle-1 font-weight-medium">Фильтры</span>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="d-flex flex-column ga-2 w-100">
                <VTextField
                  v-model="searchFilter"
                  class="w-100"
                  variant="outlined"
                  clearable
                  placeholder="Фильтр по ключевому слову"
                  :data-onboarding="AUDITS_ANCHORS.table.search"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  type="text"
                  @keyup.enter="applyAllFieldsFilter"
                />

                <VIconBtn
                  v-if="searchFilter"
                  class="w-100 rounded-sm"
                  color="primary"
                  icon="ri-filter-2-line"
                  variant="outlined"
                  :rounded="false"
                  @click="applyAllFieldsFilter"
                />

                <VBtn
                  v-if="hasActiveFilters"
                  class="w-100 text-color-secondary"
                  prepend-icon="ri-delete-bin-line"
                  variant="outlined"
                  color="secondary"
                  :loading="feedbackStore.isGlobalLoading"
                  :data-onboarding="AUDITS_ANCHORS.table.resetFilters"
                  @click="renewDataAndResetFilters"
                >
                  Сбросить фильтры
                </VBtn>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </Teleport>

      <div
        v-else
        class="d-flex align-center gap-2 w-100"
        :class="activeFilterChips.length ? 'px-4 pt-4' : 'pa-4'"
      >
          <VTextField
            v-model="searchFilter"
            class="flex-grow-0 w-25"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
          :data-onboarding="AUDITS_ANCHORS.table.search"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            type="text"
            @keyup.enter="applyAllFieldsFilter"
          />

          <VIconBtn
            v-if="searchFilter"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="applyAllFieldsFilter"
          />

          <VBtn
            v-if="hasActiveFilters"
            class="ml-auto text-color-secondary"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            :loading="feedbackStore.isGlobalLoading"
          :data-onboarding="AUDITS_ANCHORS.table.resetFilters"
            @click="renewDataAndResetFilters"
          >
            Сбросить фильтры
          </VBtn>
        </div>

      <div v-if="activeFilterChips.length" class="filter-chip d-flex ml-4 align-center gap-2 py-3">
        <VChip
          v-for="chip in activeFilterChips"
          :key="`${chip.type}-${chip.value || 'no-value'}`"
          closable
          @click:close="removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <VDataTableServer
        :data-onboarding="AUDITS_ANCHORS.table.content"
        :items="displayedAudits"
        :items-length="archiveStore.totalAuditsCount"
        :headers="headers"
        :items-per-page="itemsPerPage"
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer custom-data-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="handlePageChange"
      >
        <!-- № -->
        <template #[`header.number`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'number' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'number', 'is-desc': archiveStore.currentSortString === '-number' }"
            />
            <VIconBtn
              variant="plain"
              size="small"
              class="filter-icon-btn"
              :icon="getFilterIcon(isNumberFilterActive)"
              @click="showNumberFilter = !showNumberFilter"
            />

            <VMenu
              v-model="showNumberFilter"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard class="pa-4">
                <VTextField
                  v-model="numberFilterModal"
                  autofocus
                  placeholder="Например: 1, 2, 3"
                  hide-details
                  width="220px"
                  @keyup.enter="applyNumberFilter"
                />
                <div class="d-flex justify-end mt-2">
                  <VBtn
                    size="small"
                    variant="outlined"
                    color="secondary"
                    @click="applyNumberFilter"
                  >
                    Применить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>
          </div>
        </template>

        <!-- Норматив -->
        <template #[`header.normative`]="{ column }">
          <div class="d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'normative' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'normative', 'is-desc': archiveStore.currentSortString === '-normative' }"
            />
            <div v-if="archiveStore.filterLoading && archiveStore.filterLoading.normatives" class="d-flex align-center justify-center ml-1" style="width: 28px; height: 28px">
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>
            <VIconBtn
              v-else
              variant="plain"
              size="small"
              :icon="getFilterIcon(isNormativeFilterActive)"
              @click.stop="handleFilterClick('normatives')"
            />
            <VMenu
              v-model="filterMenus.normatives"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                field-key="normatives"
                :store="archiveStore"
                :model-value="tempSelectedFilters.normatives || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tempSelectedFilters.normatives = $event"
                @apply="applyFilter('normatives')"
              />
            </VMenu>
          </div>
        </template>

        <!-- Требование -->
        <template #[`header.requirement`]="{ column }">
          <div class="d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'requirement' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'requirement', 'is-desc': archiveStore.currentSortString === '-requirement' }"
            />
            <div v-if="archiveStore.filterLoading && archiveStore.filterLoading.requirements" class="d-flex align-center justify-center ml-1" style="width: 28px; height: 28px">
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>
            <VIconBtn
              v-else
              variant="plain"
              size="small"
              :icon="getFilterIcon(isRequirementFilterActive)"
              @click.stop="handleFilterClick('requirements')"
            />
            <VMenu
              v-model="filterMenus.requirements"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                field-key="requirements"
                :store="archiveStore"
                :model-value="tempSelectedFilters.requirements || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tempSelectedFilters.requirements = $event"
                @apply="applyFilter('requirements')"
              />
            </VMenu>
          </div>
        </template>

        <template #[`header.criteria`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <!-- Отделение -->
        <template #[`header.department`]="{ column }">
          <div class="d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'department' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'department', 'is-desc': archiveStore.currentSortString === '-department' }"
            />
            <div v-if="archiveStore.filterLoading && archiveStore.filterLoading.departments" class="d-flex align-center justify-center ml-1" style="width: 28px; height: 28px">
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>
            <VIconBtn
              v-else
              variant="plain"
              size="small"
              :icon="getFilterIcon(isDepartmentFilterActive)"
              @click.stop="handleFilterClick('departments')"
            />
            <VMenu
              v-model="filterMenus.departments"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                field-key="departments"
                :store="archiveStore"
                :model-value="tempSelectedFilters.departments || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tempSelectedFilters.departments = $event"
                @apply="applyFilter('departments')"
              />
            </VMenu>
          </div>
        </template>

        <!-- Дата нач. -->
        <template #[`header.date_start`]="{ column }">
          <div class="d-flex align-center">
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'date_start' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'date_start', 'is-desc': archiveStore.currentSortString === '-date_start' }"
            />
            <VTooltip text="Плановая">
              <template #activator="{ props }">
                <span v-bind="props">{{ column.title }}</span>
              </template>
            </VTooltip>
            <VIconBtn
              variant="plain"
              size="small"
              :icon="getFilterIcon(isDateStartFilterActive)"
              @click="showDateStartFilter = !showDateStartFilter"
            />
            <VMenu
              v-model="showDateStartFilter"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>

              <VCard class="d-flex flex-row" style="overflow: hidden">
                <!-- Панель пресетов -->
                <div class="date-presets-panel pa-2 border-e">
                  <VList density="compact" nav class="pa-0">
                    <VListItem
                      v-for="preset in datePresets"
                      :key="preset.id"
                      :title="preset.label"
                      color="primary"
                      @click="setDatePreset(preset.id, true)"
                    />
                  </VList>
                </div>

                <!-- Пикер -->
                <div class="pa-4">
                  <VTextField
                    v-model="dateStartFilterModal"
                    placeholder="ДД.ММ.ГГГГ"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="mb-3"
                    append-inner-icon="ri-calendar-line"
                    @click:append-inner="dateStartMenu = true"
                  />

                  <VMenu v-model="dateStartMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props }">
                      <div v-bind="props" />
                    </template>
                    <VDatePicker
                      hide-header
                      color="primary"
                      @update:model-value="onDateStartSelect"
                    />
                  </VMenu>

                  <VBtn
                    size="small"
                    block
                    class="mt-2"
                    @click="applyDateStartFilter"
                  >
                    Применить
                  </VBtn>
                  <VBtn
                    size="small"
                    block
                    variant="text"
                    class="mt-1"
                    color="error"
                    @click="resetDateStartFilter"
                  >
                    Сбросить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>
          </div>
        </template>

        <!-- Дата кон. -->
        <template #[`header.date_end`]="{ column }">
          <div class="d-flex align-center">
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'date_end' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'date_end', 'is-desc': archiveStore.currentSortString === '-date_end' }"
            />
            <span>{{ column.title }}</span>
            <VIconBtn
              variant="plain"
              size="small"
              :icon="getFilterIcon(isDateEndFilterActive)"
              @click="showDateEndFilter = !showDateEndFilter"
            />
            <VMenu
              v-model="showDateEndFilter"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>

              <VCard class="d-flex flex-row" style="overflow: hidden">
                <div class="date-presets-panel pa-2 border-e">
                  <VList density="compact" nav class="pa-0">
                    <VListItem
                      v-for="preset in datePresets"
                      :key="preset.id"
                      :title="preset.label"
                      color="primary"
                      @click="setDatePreset(preset.id, false)"
                    />
                  </VList>
                </div>

                <div class="pa-4">
                  <VTextField
                    v-model="dateEndFilterModal"
                    placeholder="ДД.ММ.ГГГГ"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="mb-3"
                    append-inner-icon="ri-calendar-line"
                    @click:append-inner="dateEndMenu = true"
                  />

                  <VMenu v-model="dateEndMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props }">
                      <div v-bind="props" />
                    </template>
                    <VDatePicker
                      hide-header
                      color="primary"
                      @update:model-value="onDateEndSelect"
                    />
                  </VMenu>

                  <VBtn
                    size="small"
                    block
                    class="mt-2"
                    @click="applyDateEndFilter"
                  >
                    Применить
                  </VBtn>
                  <VBtn
                    size="small"
                    block
                    variant="text"
                    class="mt-1"
                    color="error"
                    @click="resetDateEndFilter"
                  >
                    Сбросить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>
          </div>
        </template>

        <!-- Вид -->
        <template #[`header.type`]="{ column }">
          <div class="d-flex align-center">
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="archiveStore.currentSortString?.replace('-', '') === 'type' ? 'ri-arrow-up-fill' : 'ri-arrow-up-line'"
              :class="{ 'is-active': archiveStore.currentSortString?.replace('-', '') === 'type', 'is-desc': archiveStore.currentSortString === '-type' }"
            />
            <span>{{ column.title }}</span>
            <div v-if="archiveStore.filterLoading && archiveStore.filterLoading.types" class="d-flex align-center justify-center ml-1" style="width: 28px; height: 28px">
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>
            <VIconBtn
              v-else
              variant="plain"
              size="small"
              :icon="getFilterIcon(isTypeFilterActive)"
              @click.stop="handleFilterClick('types')"
            />
            <VMenu
              v-model="filterMenus.types"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                field-key="types"
                :store="archiveStore"
                :model-value="tempSelectedFilters.types || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tempSelectedFilters.types = $event"
                @apply="applyFilter('types')"
              />
            </VMenu>
          </div>
        </template>

        <template #[`header.report`]="{ column }">
          <div class="header-content">
            <span>{{ column.title }}</span>
          </div>
        </template>

        <template #[`item.number`]="{ item }">
          <span>{{ item.number }}</span>
        </template>

        <template #[`item.normative`]="{ item }">
          <span>{{ item.normative }}</span>
        </template>

        <template #[`item.requirement`]="{ item }">
          <CollapsibleCell :items="item.requirement" type-label="требований" />
        </template>

        <template #[`item.criteria`]="{ item }">
          <CollapsibleCell :items="item.criteria" type-label="критериев" />
        </template>

        <template #[`item.department`]="{ item }">
          <span>{{ item.department }}</span>
        </template>

        <template #[`item.date_start`]="{ item }">
          <span>{{ item.date_start }}</span>
        </template>

        <template #[`item.date_end`]="{ item }">
          <span>{{ item.date_end }}</span>
        </template>

        <template #[`item.type`]="{ item }">
          <span>{{ item.type }}</span>
        </template>

        <template #[`item.report`]="{ item }">
          <VBtn size="small" variant="outlined">
            Отчет
          </VBtn>
        </template>

        <template #bottom>
          <div class="custom-data-table-footer" :class="{ 'hide-next-button': hideNextButton }">
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>
  <RouterView />

  <EmployeeProfileSidebar
    v-model="isEmployeeInfoModalOpen"
    :employee-id="selectedEmployeeId ? String(selectedEmployeeId) : ''"
  />
</template>

<style scoped lang="scss">
/* ===== BASE TABLE ===== */
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  padding-inline: 1rem;
  padding-block: 0.75rem !important;
}

/* header separators */
:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

/* ===== TEXT WRAP ===== */
:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

/* long text cells */
.cell-content {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ===== ROW HOVER ===== */
:deep(.custom-data-table tr:hover) {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

/* ===== FOOTER ===== */
:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination { order: 1; }
    .v-data-table-footer__items-per-page { order: 2; }
    .v-data-table-footer__info { order: 3; }

    .v-pagination__last > button,
    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

/* ===== FIRST COLUMN COLOR ===== */
:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

/* ===== COLUMN WIDTHS (AUDIT ARCHIVE) ===== */
:deep(.v-data-table__th:nth-child(1)) { /* № */
  min-width: 6rem !important;
}

:deep(.v-data-table__th:nth-child(2)) { /* Норматив */
  min-width: 10rem !important;
}

:deep(.v-data-table__th:nth-child(3)) { /* Требование */
  min-width: 16rem !important;
}

:deep(.v-data-table__th:nth-child(4)) { /* Критерий */
  min-width: 14rem !important;
}

:deep(.v-data-table__th:nth-child(5)) { /* Отделение */
  min-width: 12rem !important;
}

:deep(.v-data-table__th:nth-child(6)) { /* Дата начала */
  min-width: 7.5rem !important;
}

:deep(.v-data-table__th:nth-child(7)) { /* Дата окончания */
  min-width: 7.5rem !important;
}

:deep(.v-data-table__th:nth-child(8)) { /* Тип */
  min-width: 9rem !important;
}

:deep(.v-data-table__th:nth-child(9)) { /* Отчет */
  min-width: 7rem !important;
}

/* ===== SORT ICONS ===== */
:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

/* ===== FILTER ICONS ===== */
.filter-icon-btn {
  opacity: 0.45;
  transition: opacity 0.2s ease;
}

:deep(.v-data-table__th:hover) .filter-icon-btn {
  opacity: 1;
}

/* ===== BUTTON IN CELL ===== */
:deep(.custom-data-table .v-btn) {
  font-size: 0.75rem;
  height: 28px;
}

/* ===== UTILITY ===== */
.cursor-pointer {
  cursor: pointer;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-presets-panel {
  width: 150px;
  background-color: rgb(var(--v-theme-surface));
}
</style>
